interface PriceScaleProps {
  price: number | null;
  highestPrice: number;
  lowestPrice: number;
  meanPrice?: number;
}

/**
 * Visual price scale showing current price relative to historical range
 * Displays a gradient bar with a circle indicating the current price position
 * Layout: low price on left (green), high price on right (red)
 */
export function PriceScale({ price, highestPrice, lowestPrice, meanPrice }: PriceScaleProps) {
  if (!price || lowestPrice === highestPrice) {
    return <div className="h-12" />;
  }

  const range = highestPrice - lowestPrice;

  // Calculate position: 0% = lowest (left), 100% = highest (right)
  const percent = 3 + ((price - lowestPrice) / range) * 94;
  const meanPercent = meanPrice ? ((meanPrice - lowestPrice) / range) * 100 : null;

  // Color: green (low price) to red (high price)
  const barColor = `hsl(${130 - percent * 1.3}, 100%, 40%)`;

  // Position from left to right
  const currentPriceCx = `${percent}%`;
  const meanPriceX = meanPercent ? `${meanPercent}%` : null;

  return (
    <div className="h-12 w-full">
      <svg
        width="100%"
        height="48"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="presentation"
      >
        {/* Background gradient bar (green on left, red on right) */}
        <defs>
          <linearGradient id="priceGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* Gradient bar */}
        <rect x="0" y="20" width="100%" height="12" fill="url(#priceGradient)" rx="5" ry="5" />

        {/* Mean price indicator (vertical dashed line) */}
        {meanPriceX && (
          <line
            x1={meanPriceX}
            y1="16"
            x2={meanPriceX}
            y2="36"
            stroke="#6b7280"
            strokeWidth="3"
            strokeDasharray="4,2"
            opacity="0.9"
          />
        )}

        {/* Current price circle indicator */}
        <circle cx={currentPriceCx} cy="26" r="9" fill={barColor} stroke="white" strokeWidth="2" />

        {/* Price labels - low on left, high on right */}
        <text x="0%" y="45" fontSize="13" fill="gray" textAnchor="end">
          ₪{lowestPrice.toLocaleString()}
        </text>
        <text x="100%" y="45" fontSize="13" fill="gray" textAnchor="start">
          ₪{highestPrice.toLocaleString()}
        </text>

        {/* Mean price label - positioned above the dashed line */}
        {meanPriceX && meanPrice && (
          <text
            x={meanPriceX}
            y="14"
            fontSize="11"
            fill="#6b7280"
            textAnchor="middle"
            fontWeight="600"
          >
            ממוצע: ₪{meanPrice.toFixed(0)}
          </text>
        )}
      </svg>
    </div>
  );
}
